// Account-holder sign-in. `role !== 'customer'` after a successful login is
// not an error from the server's point of view (the credentials were
// correct) but IS the wrong app for that person — see this file's own
// handling below and portal/src/context/AuthContext.jsx's header comment.

import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext.jsx';
import { useLanguage } from '../context/LanguageContext.jsx';
import { PasswordInput } from 'shared/components/PasswordInput/index.js';
import PortalShell from '../components/PortalShell.jsx';
import ErrorState from '../components/ErrorState.jsx';
import { Spinner } from 'shared/components/Feedback/index.js';
import { mapApiErrorId } from '../utils/errorMapping.js';
import styles from './LoginPage.module.css';

const KNOWN_LOGIN_ERROR_CODES = ['INVALID_CREDENTIALS', 'ACCOUNT_INACTIVE'];

export default function LoginPage() {
  const { user, login, isLoggingIn, loginError, resetLoginError, logout } = useAuth();
  const { t, lang } = useLanguage();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues: { email: '', password: '' } });

  // A session already exists for a non-customer role (e.g. this browser
  // profile also has a live sales/admin session — a real possibility in a
  // dev environment where admin/ and portal/ share a cookie domain). This
  // app has no legitimate use for that session, so it's torn down rather
  // than left sitting around looking usable.
  //
  // `wrongRoleMessage` is deliberately its own latched state, NOT derived
  // live from `user` — a real bug caught only by actually driving this
  // screen end to end (not by reading the code back): deriving it as
  // `Boolean(user) && user.role !== 'customer'` meant the moment the
  // defensive `logout()` call below completed, `user` reverted to `null`,
  // that derived condition flipped back to `false`, and the message this
  // screen exists to show disappeared again — a real login attempt with the
  // wrong role would flash the message for a moment and then silently fall
  // back to a bare, misleading login form. Latching it into its own state
  // (set once, cleared only when the user edits a field to try again) means
  // it survives the logout that necessarily clears `user`.
  const [wrongRoleMessage, setWrongRoleMessage] = useState(false);

  useEffect(() => {
    if (user && user.role !== 'customer' && !wrongRoleMessage) {
      setWrongRoleMessage(true);
      logout().catch(() => {
        // Non-fatal — the wrong-role message still renders either way; a
        // failed logout call just means the stale session lingers
        // server-side a little longer, not a reason to block this screen.
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const clearWrongRoleMessage = () => {
    if (wrongRoleMessage) setWrongRoleMessage(false);
    resetLoginError();
  };

  // "Try a different account" — the effect above already fires a best-effort
  // logout the moment a non-buyer session is detected, but that call is
  // fire-and-forget and can fail. This button makes the teardown explicit:
  // it awaits logout() (retrying it if the effect's attempt failed) BEFORE
  // returning to the form, so a stale staff session is actually gone, not
  // just visually dismissed. AuthContext.logout() clears the me-query and
  // the whole react-query cache on success.
  const [isClearingSession, setIsClearingSession] = useState(false);
  const handleTryDifferentAccount = async () => {
    setIsClearingSession(true);
    try {
      await logout();
    } catch {
      // Best-effort. Even if this fails, every real portal API still
      // enforces requireRole('customer') server-side, and the next
      // /auth/me refetch surfaces the stale session and routes back here.
    } finally {
      setIsClearingSession(false);
    }
    setWrongRoleMessage(false);
    resetLoginError();
  };

  if (user && user.role === 'customer') {
    return <Navigate to="/request" replace />;
  }

  const onSubmit = async (values) => {
    try {
      await login(values);
    } catch {
      // loginError (from useAuth) already carries the ApiError for display
      // below.
    }
  };

  const serverErrorId = loginError ? mapApiErrorId(loginError, KNOWN_LOGIN_ERROR_CODES) : null;

  return (
    <PortalShell backTo="/" width="narrow">
      {wrongRoleMessage ? (
        <div className={styles.wrongRole}>
          <p className={styles.wrongRoleTitle}>{t('portal.login.notBuyerAccount.title')}</p>
          <p className={styles.wrongRoleBody}>{t('portal.login.notBuyerAccount.body')}</p>
          <button
            type="button"
            className={styles.tryAgainButton}
            onClick={handleTryDifferentAccount}
            disabled={isClearingSession}
          >
            {isClearingSession && <Spinner />}{' '}
            {isClearingSession ? t('portal.login.tryAgainClearing') : t('portal.login.tryAgain')}
          </button>
          <Link to="/start" className={styles.linkButton}>
            {t('portal.login.guestLink')}
          </Link>
          <Link to="/track" className={styles.linkButton}>
            {t('portal.login.trackExistingLink')}
          </Link>
        </div>
      ) : (
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
          <h1 className={styles.title}>{t('portal.login.title')}</h1>
          <p className={styles.subtitle}>{t('portal.login.subtitle')}</p>

          <label className={styles.field}>
            <span className={styles.label}>{t('portal.login.email')}</span>
            <input
              type="email"
              autoComplete="username"
              className={styles.input}
              {...register('email', { required: true, onChange: clearWrongRoleMessage })}
            />
            {errors.email && (
              <span className={styles.fieldError}>{t('error.VALIDATION_ERROR')}</span>
            )}
          </label>

          <label className={styles.field}>
            <span className={styles.label}>{t('portal.login.password')}</span>
            <PasswordInput
              lang={lang}
              autoComplete="current-password"
              {...register('password', { required: true, onChange: clearWrongRoleMessage })}
            />
            {errors.password && (
              <span className={styles.fieldError}>{t('error.VALIDATION_ERROR')}</span>
            )}
          </label>

          {serverErrorId && <ErrorState messageId={serverErrorId} error={loginError} />}

          <button type="submit" className={styles.submitButton} disabled={isLoggingIn}>
            {isLoggingIn && <Spinner />}{' '}
            {isLoggingIn ? t('portal.login.submitting') : t('portal.login.submit')}
          </button>

          <Link to="/forgot-password" className={styles.linkButton}>
            {t('portal.login.forgotPassword')}
          </Link>

          <div className={styles.altActions}>
            <p className={styles.altPrompt}>
              {t('portal.login.noAccountPrompt')}{' '}
              <Link to="/register" className={styles.inlineLink}>
                {t('portal.login.registerLink')}
              </Link>
            </p>
            <Link to="/start" className={styles.linkButton}>
              {t('portal.login.guestLink')}
            </Link>
            <Link to="/track" className={styles.linkButton}>
              {t('portal.login.trackExistingLink')}
            </Link>
          </div>
        </form>
      )}
    </PortalShell>
  );
}
